import { Assets, Container, Polygon, type Sprite, type Texture } from 'pixi.js';
import { BIOMES, WORLD_NODES, isSlotUnlocked, slotGemPrice } from '@goodtime/shared';
import {
  PLATE_SHIFT,
  PaintedMapScene,
  boxOf,
  type MapInsets,
  type PaintedMapOptions,
} from './PaintedMapScene';
import { minZoom, visibleRect } from '../logic/realm/camera';
import { backAnchor, crownAnchor, listAnchor } from '../logic/realm/mapHud';
import {
  WORLD_PLATE,
  WORLD_PLATE_NARROW,
  boundsOf,
  buildingStops,
  roadLitTo,
  nearestBuilding,
  plateIsNarrow,
  plateSpot,
  platesTight,
  tapAreaOf,
  worldMapOf,
  type WorldBuilding,
  type WorldMapSpec,
} from '../logic/realm/worlds';
import { BezelButton, RealmPlaque, drawCrown } from '../widgets/realm/RealmPieces';
import { softDot } from '../widgets/realm/RealmAmbient';
import { LANTERN_STEP_MS, LockedVeil, RoadLights } from '../widgets/realm/WorldRoad';

/** The world map's own HUD art, by URL. */
export interface WorldMapHudArt {
  /** The multiplayer Wheel's crown medallion. */
  crown: string;
  gift: string;
  /** The list button's picture (the board of every slot). */
  list: string;
  /** The plates' stars. */
  star: string;
  /** The way back to every world: the painted map, as on the slots' map button. */
  map: string;
}

/** What a slot's plate shows besides its name and price. */
export interface WorldSlotInfo {
  /** Stars earned, 0 to 3. */
  stars?: number;
  /** A road slot's chapter: its number and its landmarks restored. */
  chapter?: { n: number; restored: number; of: number };
  /** While locked, it opens free when this chapter is done (the world's
   *  second slot, and Castle Gardens for Royal Reels' chapter). */
  freeAfterChapter?: number;
}

export interface WorldMapSceneOptions extends PaintedMapOptions {
  /** The world (index into BIOMES): one with a map ({@link worldMapOf}). */
  world: number;
  hud: WorldMapHudArt;
  /** The slot to centre on as the map opens (the one just played, or the
   *  building tapped on the overview); else the slot the player was last
   *  on, if it's in this world, else the world's first. */
  focusNodeId?: number | null;
  /** Each slot's stars and road, by node id ({@link WorldMapScene.setSlots}
   *  when they arrive). */
  slots?: ReadonlyMap<number, WorldSlotInfo>;
  /** The open slots this world's map showed last time, or null when it has
   *  never shown on this device: a slot open since gets its opening moment. */
  seenOpen?: readonly number[] | null;
  /** The map has now shown these open slots (an opening moment began). */
  onSeenOpen?: (ids: number[]) => void;
  /** Back to the realm map. */
  onBack: () => void;
  /** The world's multiplayer Wheel. */
  onWheel?: () => void;
  /** The daily gift. */
  onGift?: () => void;
  giftReady?: boolean;
}

/** How long colour takes to flow back into a building that has opened. */
const COLOUR_MS = 1300;

/** An opening moment: colour flows back into a building, after the lanterns
 *  have lit along the road to it. */
interface Moment {
  building: number;
  /** Before the colour starts (the lanterns are on their way). */
  wait: number;
  /** 0 to 1 through the colour's return. */
  t: number;
}

/**
 * One world's own painted map (flag `world_maps`): its land, its road
 * winding left to right past its seven slots in order, each slot a
 * building to tap. The panning, zooming, tiles, plates, floors, ambient
 * life, board and the social buttons are the painted map's
 * (PaintedMapScene); a world map adds:
 * - plates with each slot's stars, its chapter on a road slot, and on a
 *   locked second slot the chapter that opens it free;
 * - locked buildings dimmed and greyed ({@link LockedVeil}), the road's
 *   lanterns lit as far as the furthest open slot and a soft glow on the
 *   next to open ({@link RoadLights}), and the opening moment when a slot
 *   has opened since the map last showed;
 * - the HUD: back to the realm, the crown and the gift, and the list
 *   button along the bottom (the board). There is no switcher in a world:
 *   the realm's names the world in view, and the buildings are tapped on
 *   the map.
 *
 * Ambient life goes in {@link ambientLayer} as on the realm (the options'
 * `ambient`), with the trees' sway and the river's ripple in the paint
 * layer under the road's lights and the locked veil. The life that belongs
 * to a building (its lights, the dragon on its hoard) goes dark or grey
 * with it while it's locked, and comes back in its opening moment.
 */
export class WorldMapScene extends PaintedMapScene<WorldMapSceneOptions> {
  protected override readonly logName = 'WorldMapScene';
  readonly spec: WorldMapSpec;
  private slots: ReadonlyMap<number, WorldSlotInfo>;
  private tight = false;
  private readonly still: boolean;
  private readonly roadLayer = new Container();
  private veil: LockedVeil | null = null;
  private lights: RoadLights | null = null;
  private glow: Texture | null = null;
  private back: BezelButton | null = null;
  private list: BezelButton | null = null;
  private crown: Container | null = null;
  private gift: BezelButton | null = null;
  private hudArt = new Map<keyof WorldMapHudArt, Texture | null>();
  /** The open slots the map has shown, or null before it ever has. */
  private seen: Set<number> | null;
  /** Slots opened since the map last showed, waiting for their moment. */
  private waiting: number[] = [];
  private moments: Moment[] = [];
  /** The slots whose opening moment has played on this visit (tests). */
  private readonly shown: string[] = [];

  constructor(container: HTMLElement, opts: WorldMapSceneOptions) {
    const spec = worldMapOf(opts.world);
    if (!spec) throw new Error(`No world map for world ${opts.world}`);
    super(container, opts, spec.tiles, opts.world);
    this.spec = spec;
    this.slots = opts.slots ?? new Map();
    this.seen = opts.seenOpen ? new Set(opts.seenOpen) : null;
    this.still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  /** The building the map opens on. */
  private focusIndex(): number {
    const { focusNodeId, currentNodeId } = this.opts;
    const by = (id: number | null | undefined): number =>
      id === null || id === undefined ? -1 : this.spec.buildings.findIndex((b) => b.node === id);
    const f = by(focusNodeId);
    if (f >= 0) return f;
    return Math.max(0, by(currentNodeId));
  }

  protected override async preload(): Promise<void> {
    const hud = Object.entries(this.opts.hud) as [keyof WorldMapHudArt, string][];
    await Promise.all([
      super.preload(),
      ...hud.map(async ([key, url]) => {
        this.hudArt.set(key, await Assets.load<Texture>(url).catch(() => null));
      }),
    ]);
  }

  private hudTexture(key: keyof WorldMapHudArt): Texture | null {
    return this.hudArt.get(key) ?? null;
  }

  // ---- the player's slots ----

  /** Each slot's stars and road arrived or changed. */
  setSlots(slots: ReadonlyMap<number, WorldSlotInfo>): void {
    this.slots = slots;
    if (this.state !== 'initialized') return;
    this.buildPlates();
    this.placeNames();
  }

  /** The daily gift is there to claim, or isn't. */
  setGiftReady(ready: boolean): void {
    if (this.gift) this.gift.visible = ready;
    if (this.state === 'initialized') this.layoutHud();
  }

  private isOpen(b: WorldBuilding): boolean {
    return isSlotUnlocked(b.node, this.unlocked);
  }

  // ---- the buildings ----

  protected override buildPlates(): void {
    for (const c of this.landmarkLayer.removeChildren()) c.destroy({ children: true });
    this.plates = [];
    const plaques: RealmPlaque[] = [];
    const art = {
      lock: this.texture('lock'),
      gem: this.texture('gem'),
      star: this.hudTexture('star'),
      floor: this.floors.loaded(this.opts.world),
    };
    this.tight = platesTight(this.spec, this.cam.zoom);
    for (const b of this.spec.buildings) {
      const node = WORLD_NODES.find((n) => n.id === b.node);
      if (!node) continue;
      const open = this.isOpen(b);
      const hit = new Container();
      hit.label = `landmark:${b.name}`;
      hit.hitArea = new Polygon(tapAreaOf(b).flat());
      hit.eventMode = 'static';
      hit.cursor = 'pointer';
      hit.on('pointertap', () => this.tapSlot(node));
      this.landmarkLayer.addChild(hit);
      const plaque = new RealmPlaque(
        b.name,
        open,
        open ? null : slotGemPrice(b.node),
        b.node === this.opts.currentNodeId,
        art,
        this.plateWords(b, open, plateIsNarrow(this.spec, b, this.cam.zoom)),
      );
      plaque.label = `plaque:${b.name}`;
      plaque.scale.set(1 / this.cam.zoom);
      plaque.eventMode = 'static';
      plaque.cursor = 'pointer';
      plaque.on('pointertap', () => this.tapSlot(node));
      plaques.push(plaque);
      const [mx, my] = plateSpot(this.spec, b, this.cam.zoom);
      this.plates.push({
        item: plaque,
        mx,
        my,
        drop: 0,
        box: boxOf(plaque),
        maxShift: PLATE_SHIFT,
        tappable: true,
        slide: false,
        outline: boundsOf(b.outline),
        pullClear: this.spec.buildings.filter((q) => q !== b).flatMap((q) => [q.outline, tapAreaOf(q)]),
        shown: false,
        fresh: true,
        world: this.opts.world,
      });
    }
    for (const p of plaques) this.landmarkLayer.addChild(p);
    this.refreshLocks();
  }

  /** A plate's second line: an open slot's stars (and a road slot's
   *  chapter); a locked second slot's chapter that opens it free. */
  private plateWords(
    b: WorldBuilding,
    open: boolean,
    narrow = false,
  ): { stars?: number; line?: string; maxWidth: number; narrow?: boolean } {
    const info = this.slots.get(b.node);
    // A narrow plate (at the tight zoom, for a building with no room for a wide one) holds
    // the name, then the lock and price or the stars: no chapter words.
    if (narrow) {
      return { stars: open ? (info?.stars ?? 0) : undefined, maxWidth: WORLD_PLATE_NARROW.w, narrow };
    }
    const maxWidth = WORLD_PLATE.w;
    if (open) {
      const ch = info?.chapter;
      const line = !ch
        ? undefined
        : ch.restored >= ch.of
          ? `Chapter ${ch.n} done`
          : `Chapter ${ch.n} · ${ch.restored} of ${ch.of}`;
      return { stars: info?.stars ?? 0, line, maxWidth };
    }
    if (info?.freeAfterChapter) {
      return { line: `Free when Chapter ${info.freeAfterChapter} is done`, maxWidth };
    }
    return { maxWidth };
  }

  protected override openingSpot(): { x: number; y: number } {
    const b = this.spec.buildings[this.focusIndex()]!;
    return { x: b.centre[0] / this.map.w, y: b.centre[1] / this.map.h };
  }

  /** A world map opens as far out as it goes: the whole world across on a
   *  desktop, its whole height on a phone (panning along the road). */
  protected override openingZoom(): number {
    return minZoom(this.map, this.view);
  }

  protected override buildingAt(name: string): { x: number; y: number } | null {
    const b = this.spec.buildings.find((q) => q.name === name);
    return b ? { x: b.centre[0] / this.map.w, y: b.centre[1] / this.map.h } : null;
  }

  // ---- locked buildings, the lanterns and the opening moment ----

  protected override buildMore(): void {
    this.world.addChildAt(this.roadLayer, this.world.getChildIndex(this.ambientLayer));
    this.veil = new LockedVeil(this.roadLayer, this.spec);
    this.glow = softDot();
    this.lights = new RoadLights(this.roadLayer, this.spec, this.glow, this.still);
    this.buildHudMore();
    this.refreshLocks();
  }

  /**
   * Grey the locked buildings and light the road to the furthest open slot.
   * A slot open now that the map hasn't shown open waits, greyed, for its
   * moment ({@link tick} plays it once the map is in view).
   */
  private refreshLocks(): void {
    const veil = this.veil;
    const lights = this.lights;
    if (!veil || !lights) return;
    const open = this.spec.buildings.map((b) => this.isOpen(b));
    const seen = this.seen;
    if (seen) {
      for (const [i, b] of this.spec.buildings.entries()) {
        const playing = this.moments.some((m) => m.building === i);
        if (open[i] && !seen.has(b.node) && !playing && !this.waiting.includes(i)) {
          this.waiting.push(i);
        }
      }
    } else {
      // Never shown here: nothing to celebrate, only to remember.
      this.markSeen();
    }
    this.waiting = this.waiting.filter((i) => open[i]);
    const levels = open.map((o, i) => {
      if (!o || this.waiting.includes(i)) return 1;
      const m = this.moments.find((q) => q.building === i);
      return m ? 1 - m.t : 0;
    });
    veil.setLevels(levels);
    this.lockLife(levels);
    // A moment under way lights its own lanterns.
    if (!this.moments.length) lights.light(this.litTo(open.map((o, i) => o && !this.waiting.includes(i))));
    const next = this.spec.buildings.findIndex((_, i) => !open[i]);
    const at = next >= 0 ? this.spec.buildings[next]!.centre : null;
    lights.setNext(at ? { x: at[0], y: at[1] } : null);
  }

  /** How far along the road the lanterns are lit: to the furthest of
   *  `lit`'s slots, and to the road's end once every slot is lit. */
  private litTo(lit: readonly boolean[]): number {
    return roadLitTo(this.spec, lit);
  }

  private markSeen(): void {
    const ids = this.spec.buildings.filter((b) => this.isOpen(b)).map((b) => b.node);
    this.seen = new Set(ids);
    this.opts.onSeenOpen?.(ids);
  }

  /** Start the waiting slots' moments: the lanterns light along the road to
   *  each, then colour flows back into it. */
  private startMoments(): void {
    const lights = this.lights;
    if (!lights || this.waiting.length === 0) return;
    const open = this.spec.buildings.map((b) => this.isOpen(b));
    const before = this.litTo(open.map((o, i) => o && !this.waiting.includes(i)));
    const after = this.litTo(open);
    lights.light(after, before);
    const stops = buildingStops(this.spec);
    const lanterns = lights.lanterns();
    for (const i of this.waiting) {
      // The colour starts as the lanterns reach the building.
      const passed = lanterns.filter((l) => l.s > before && l.s <= stops[i]! + 40).length;
      this.moments.push({ building: i, wait: this.still ? 0 : passed * LANTERN_STEP_MS, t: 0 });
      this.shown.push(this.spec.buildings[i]!.name);
    }
    this.waiting = [];
    this.markSeen();
  }

  protected override tick(ms: number): void {
    const view = visibleRect(this.cam, this.view);
    if (this.waiting.length && this.state === 'initialized' && !this.mapCovered()) {
      this.startMoments();
    }
    if (this.moments.length && this.veil) {
      for (const m of this.moments) {
        if (m.wait > 0) m.wait -= ms;
        else m.t = this.still || m.t + ms / COLOUR_MS > 0.999 ? 1 : m.t + ms / COLOUR_MS;
      }
      const veil = this.veil;
      const levels = this.spec.buildings.map((_, i) => {
        const m = this.moments.find((q) => q.building === i);
        return m ? 1 - m.t : veil.level(i);
      });
      this.moments = this.moments.filter((m) => m.t < 1);
      veil.setLevels(levels);
      this.lockLife(levels);
    }
    this.lights?.update(ms, !this.mapCovered());
    this.veil?.update(view);
  }

  /** The life that belongs to a building (its lights, the dragon) goes dark
   *  or grey with it: each building's level, in slot order. */
  private lockLife(levels: readonly number[]): void {
    this.setAmbientLocks(new Map(this.spec.buildings.map((b, i) => [b.node, levels[i] ?? 0])));
  }

  protected override ambientMade(): void {
    const veil = this.veil;
    if (veil) this.lockLife(this.spec.buildings.map((_, i) => veil.level(i)));
  }

  protected override tilesChanged(tiles: ReadonlyMap<string, Sprite>): void {
    this.veil?.sync(tiles);
  }

  /** A world's map has no switcher: the realm's names the world in view. */
  protected override hasSwitcher(): boolean {
    return false;
  }

  protected override cameraMoved(zoom: number): void {
    const tight = platesTight(this.spec, zoom);
    if (tight === this.tight) return;
    this.tight = tight;
    // A narrow plate is a different plate: build them again for the new zoom.
    if (this.state === 'initialized' && this.spec.buildings.some((b) => b.plateNarrow)) {
      this.buildPlates();
      return;
    }
    this.plates.forEach((p, i) => {
      const b = this.spec.buildings[i];
      if (!b) return;
      [p.mx, p.my] = plateSpot(this.spec, b, zoom);
    });
  }

  // ---- the HUD ----

  private buildHudMore(): void {
    const bezel = this.texture('bezel');
    // The way back to the worlds is the painted map in the ring, as the slots' own map button (producer 2026-11-03).
    const map = this.hudTexture('map');
    this.back = new BezelButton(bezel, map ?? this.texture('arrow'), 'Worlds', () => this.opts.onBack(), map ? 38 : 26);
    this.list = new BezelButton(bezel, this.hudTexture('list'), 'All games', () =>
      this.openBoard(this.opts.world),
    );
    const crown = new Container();
    crown.eventMode = 'static';
    crown.cursor = 'pointer';
    crown.on('pointertap', () => this.opts.onWheel?.());
    drawCrown(crown, this.hudTexture('crown'));
    this.crown = crown;
    this.gift = new BezelButton(bezel, this.hudTexture('gift'), 'Gift', () => this.opts.onGift?.());
    this.gift.visible = !!this.opts.giftReady;
    this.hud.addChild(this.back, this.list, crown, this.gift);
  }

  /** Back sits where Friends would: the social buttons move down one. */
  protected override socialDrop(): number {
    return 74;
  }

  protected override layoutMore(w: number, h: number, inset: MapInsets): Container[][] {
    const groups: Container[][] = [];
    if (this.back) {
      this.back.position.set(...backAnchor(inset));
      groups.push([this.back]);
    }
    if (this.list) {
      // Where the switcher sits on the realm's map: bottom middle.
      this.list.position.set(...listAnchor({ w, h }, inset));
      groups.push([this.list]);
    }
    if (this.crown && this.gift) {
      const [x, y] = crownAnchor({ w, h }, inset);
      this.crown.position.set(x, y);
      this.gift.position.set(x, y + 84);
      groups.push([this.crown, this.gift].filter((c) => c.visible));
    }
    return groups;
  }

  protected override moreControls(out: Map<string, Container>): void {
    if (this.back) out.set('back', this.back);
    if (this.list) out.set('list', this.list);
    if (this.crown) out.set('wheel', this.crown);
    if (this.gift) out.set('gift', this.gift);
  }

  protected override disposeMore(): void {
    this.veil?.destroy();
    this.veil = null;
    this.lights?.destroy();
    this.lights = null;
    this.glow?.destroy(true);
    this.glow = null;
  }

  // ---- test and dev hooks ----

  /** The building the map opened on. */
  openedOn(): string {
    return this.spec.buildings[this.focusIndex()]?.name ?? '';
  }

  /** Glide to a building, as a player's drag would (tests): at least the
   *  opening zoom, so its plate is in view. */
  showBuilding(name: string): void {
    const b = this.spec.buildings.find((x) => x.name === name);
    if (b) this.focus(b.centre[0], b.centre[1], Math.max(this.cam.zoom, this.openingZoom()));
  }

  /** The building nearest the middle of the screen. */
  centredOn(): string {
    const r = visibleRect(this.cam, this.view);
    const i = nearestBuilding(
      this.spec,
      (r.x + r.w / 2) / this.map.w,
      (r.y + r.h / 2) / this.map.h,
    );
    return this.spec.buildings[i]?.name ?? '';
  }

  /** Each building's plate as shown: on screen or not, where (CSS pixels),
   *  its words and stars, and whether it glows as the current slot. */
  platesUi(): {
    name: string;
    shown: boolean;
    open: boolean;
    here: boolean;
    stars: number | null;
    line: string;
    rect: { x: number; y: number; w: number; h: number };
  }[] {
    return this.plates.map((p, i) => {
      const b = this.spec.buildings[i]!;
      const plaque = p.item as RealmPlaque;
      const r = plaque.getBounds();
      return {
        name: b.name,
        shown: p.shown,
        open: this.isOpen(b),
        here: b.node === this.opts.currentNodeId,
        stars: plaque.stars,
        line: plaque.line,
        rect: { x: r.x, y: r.y, w: r.width, h: r.height },
      };
    });
  }

  /** Each building: open or locked, how grey it is now (1 locked, 0 in
   *  colour), and whether it's the next to open (the soft glow). */
  buildingsUi(): { name: string; open: boolean; grey: number; next: boolean }[] {
    const next = this.spec.buildings.findIndex((b) => !this.isOpen(b));
    return this.spec.buildings.map((b, i) => ({
      name: b.name,
      open: this.isOpen(b),
      grey: this.veil?.level(i) ?? 0,
      next: i === next,
    }));
  }

  /** The road's lanterns: how many are lit, of how many, and how far along
   *  the road the furthest lit one stands. */
  lanternsUi(): { lit: number; total: number; furthest: number } {
    const all = this.lights?.lanterns() ?? [];
    const lit = all.filter((l) => l.level > 0.5);
    return { lit: lit.length, total: all.length, furthest: Math.max(0, ...lit.map((l) => l.s)) };
  }

  /** The opening moments: the slots shown opening on this visit, and
   *  whether one is still playing. */
  momentsUi(): { shown: string[]; playing: boolean; waiting: number } {
    return { shown: [...this.shown], playing: this.moments.length > 0, waiting: this.waiting.length };
  }

  /** The world's name and slots, as the overview's world plate counts them. */
  worldUi(): { name: string; open: number; total: number } {
    return {
      name: BIOMES[this.opts.world]?.name ?? '',
      open: this.spec.buildings.filter((b) => this.isOpen(b)).length,
      total: this.spec.buildings.length,
    };
  }
}
